import { AmountInputField, FormDivider, MonthlyPayment, SelectField } from '@hex/calculators-shared/components';
import { useSapphireMedia } from '@hex/calculators-shared/hooks';
import { LoanType } from '@hex/calculators-shared/types/affordability';
import { Button, Flex, Icon, SelectItem, View, tokens } from '@sapphire-react/core';
import { Subtract, Add, Sack, Document } from '@sapphire/icons/react';
import { Fragment } from 'react';
import { type Control, type UseFormWatch, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type Props = {
  control: Control<any>;
  watch: UseFormWatch<any>;
};

export function AddLoanCredit({ control, watch }: Props) {
  const { t } = useTranslation();

  const { fields, remove, append } = useFieldArray({
    control,
    name: 'debts',
  });
  const { isExtraSmall, isSmall } = useSapphireMedia();
  const inputSize = isExtraSmall || isSmall ? 'md' : 'lg';

  const loanTypeRules = { required: t('personalInformation.inputs.loanCredit.requiredError') };
  const remainingDebtRules = {
    required: t('personalInformation.inputs.remainingDebt.requiredError', { debtAmount: 0 }),
  };
  const monthlyPaymentRules = { required: t('personalInformation.inputs.monthlyStudentPayment.requiredError') };
  const creditLimitRules = { required: t('personalInformation.inputs.creditLimit.requiredError') };

  return (
    <Flex flexDirection={'column'} gap={tokens.size.spacingMd}>
      {fields.map((field, index) => {
        const loanType = watch(`debts.${index}.loanType`);
        const monthlyPayment = watch(`debts.${index}.monthlyPayment`);

        return (
          <Fragment key={field.id}>
            <View>
              <Button
                variant={'text'}
                icon={<Subtract />}
                iconAlign={'left'}
                marginBottom={tokens.size.spacing2xs}
                onPress={() => remove(index)}
                data-test-id={`debts.${index}.remove`}
              >
                {t('removeLoanCredit')}
              </Button>

              <View display={'flex'} flexDirection={'column'} gap={tokens.size.spacingMd} marginBottom={16}>
                <SelectField
                  control={control}
                  name={`debts.${index}.loanType`}
                  placeholder={t('personalInformation.inputs.loanCredit.placeholder')}
                  rules={loanTypeRules}
                  label={t('personalInformation.inputs.loanCredit.label')}
                  width={'100%'}
                  prefix={
                    <Icon size={inputSize}>
                      <Document />
                    </Icon>
                  }
                >
                  <SelectItem key={LoanType.CreditCard}>
                    {t('personalInformation.inputs.loanCredit.options.creditCard')}
                  </SelectItem>
                  <SelectItem key={LoanType.Guarantee}>
                    {t('personalInformation.inputs.loanCredit.options.guarantee')}
                  </SelectItem>
                  <SelectItem key={LoanType.StudentLoan}>
                    {t('personalInformation.inputs.loanCredit.options.studentLoan')}
                  </SelectItem>
                  <SelectItem key={LoanType.OtherLoan}>
                    {t('personalInformation.inputs.loanCredit.options.otherLoan')}
                  </SelectItem>
                </SelectField>
                {[LoanType.Guarantee, LoanType.OtherLoan].includes(loanType) && (
                  <AmountInputField
                    control={control}
                    name={`debts.${index}.remainingDebt`}
                    label={t('personalInformation.inputs.remainingDebt.label')}
                    rules={remainingDebtRules}
                    width={'100%'}
                    postfix={'kr'}
                    min={0}
                    max={999_999_999}
                    prefix={
                      <Icon size={inputSize}>
                        <Sack />
                      </Icon>
                    }
                  />
                )}
                {[LoanType.CreditCard].includes(loanType) && (
                  <AmountInputField
                    control={control}
                    name={`debts.${index}.creditLimit`}
                    label={t('personalInformation.inputs.creditLimit.label')}
                    rules={creditLimitRules}
                    width={'100%'}
                    postfix={'kr'}
                    min={0}
                    max={999_999_999}
                    prefix={
                      <Icon size={inputSize}>
                        <Sack />
                      </Icon>
                    }
                  />
                )}
                {[LoanType.StudentLoan].includes(loanType) && (
                  <MonthlyPayment
                    control={control}
                    name={`debts.${index}.monthlyPayment`}
                    label={t('personalInformation.inputs.monthlyStudentPayment.label')}
                    rules={monthlyPaymentRules}
                    fieldFormValue={monthlyPayment}
                  />
                )}
              </View>
            </View>
            <FormDivider marginBottom={8} marginTop={0} />
          </Fragment>
        );
      })}
      <Button
        variant={'text'}
        icon={<Add />}
        iconAlign={'left'}
        data-test-id={'add-debt-button'}
        onPress={() =>
          append({ loanType: undefined, creditLimit: undefined, remainingDebt: undefined, monthlyPayment: undefined })
        }
      >
        {fields.length > 0 ? t('addOtherLoanCredit') : t('addLoanCredit')}
      </Button>
    </Flex>
  );
}
